import { z } from 'zod'

/**
 * Ausgabevorlagen fuer Gutachten (RNA M7).
 *
 * Die Antwort liefert die Luecken mit, und das ist Absicht: `status: 'leer'`
 * je Abschnitt und die Liste der offenen Punkte sind das Ergebnis, nicht ein
 * Fehler. Wo ein Punkt nicht aus abgerufenen Belegen zu stuetzen ist, bleibt
 * er leer und erscheint unter «Offene Punkte» — ein halb gefuellter Bericht
 * ist brauchbar, ein erfundener ist ein Haftungsrisiko.
 *
 * `status: 'annahme'` gibt es nur in der Vorstudie und im Zwischenbericht. Eine
 * Annahme traegt nie eine Quellenangabe (EX_ Regel 4); der Endbericht
 * uebernimmt sie nicht, sondern fuehrt sie als offenen Punkt.
 */

export const gutachtenVorlageSchema = z.enum(['vorstudie', 'zwischenbericht', 'endbericht'])
export type GutachtenVorlageValue = z.infer<typeof gutachtenVorlageSchema>

/** Die vier zulaessigen Zuordnungen. `ungeklaert` ist eine Antwort, keine Luecke. */
export const haftungZuordnungSchema = z.enum(['ausfuehrung', 'planung', 'nutzung', 'ungeklaert'])
export type HaftungZuordnungValue = z.infer<typeof haftungZuordnungSchema>

export const rechtsraumSchema = z.enum(['ch', 'fl'])
export type RechtsraumValue = z.infer<typeof rechtsraumSchema>

export const gutachtenAbschnittIdSchema = z.enum([
  'feststellung',
  'ursache',
  'normbezug',
  'haftungszuordnung',
  'fotodokumentation',
  'offene_punkte',
])
export type GutachtenAbschnittId = z.infer<typeof gutachtenAbschnittIdSchema>

export const gutachtenAbschnittStatusSchema = z.enum(['belegt', 'annahme', 'leer'])
export type GutachtenAbschnittStatus = z.infer<typeof gutachtenAbschnittStatusSchema>

/** Ein belegpflichtiger Befund: ohne Fundstellen wird er nicht uebernommen. */
export const gutachtenBefundSchema = z.object({
  text: z.string().min(1).max(4000),
  fundstellen: z.array(z.string().min(1).max(400)).max(20),
  annahme: z.boolean(),
})

export const gutachtenFotoSchema = z.object({
  datei: z.string().min(1).max(400),
  datum: z.string().min(1).max(40).nullable(),
  ort_im_objekt: z.string().min(1).max(200).nullable(),
  feststellung: z.string().min(1).max(1000).nullable(),
})

export const gutachtenRequestSchema = z.object({
  vorlage: gutachtenVorlageSchema,
  fragestellung: z.string().min(1).max(4000),
  objekt: z.object({
    bezeichnung: z.string().min(1).max(300),
    ausfuehrungsjahr: z.number().int().min(1800).max(2200).nullable(),
    sanierungsjahr: z.number().int().min(1800).max(2200).nullable(),
    rechtsraum: rechtsraumSchema.nullable(),
  }),
  bauteil: z.string().min(1).max(200).nullable(),
  schadensbild: z.string().min(1).max(200).nullable(),
  normfamilien: z.array(z.string().min(1).max(120)).max(10),
  feststellungen: z.array(z.string().min(1).max(4000)).max(50),
  ursache: gutachtenBefundSchema.nullable(),
  haftung: gutachtenBefundSchema.extend({ zuordnung: haftungZuordnungSchema }).nullable(),
  fotos: z.array(gutachtenFotoSchema).max(60),
  weitere_offene_punkte: z.array(z.string().min(1).max(1000)).max(50),
})
export type GutachtenRequest = z.infer<typeof gutachtenRequestSchema>

export const gutachtenVorlagenResponseSchema = z.object({
  vorlagen: z.array(
    z.object({
      id: gutachtenVorlageSchema,
      titel: z.string(),
      untertitel: z.string(),
      zweck: z.string(),
      belegtiefe: z.enum(['annahmen_erlaubt', 'vollbeleg']),
      abschnitte: z.array(gutachtenAbschnittIdSchema),
    }),
  ),
})
export type GutachtenVorlagenResponse = z.infer<typeof gutachtenVorlagenResponseSchema>

export const gutachtenResponseSchema = z.object({
  vorlage: gutachtenVorlageSchema,
  titel: z.string(),
  markdown: z.string(),
  abschnitte: z.array(
    z.object({
      id: gutachtenAbschnittIdSchema,
      titel: z.string(),
      status: gutachtenAbschnittStatusSchema,
      fundstellen: z.array(z.string()),
    }),
  ),
  offene_punkte: z.array(
    z.object({
      abschnitt: gutachtenAbschnittIdSchema.nullable(),
      text: z.string(),
      grund: z.string().nullable(),
    }),
  ),
  fundstellen: z.array(
    z.object({
      path: z.string(),
      titel: z.string(),
      label: z.string(),
      verwendbar: z.boolean(),
      vorbehalt: z.string().nullable(),
      verwendet_in: z.array(gutachtenAbschnittIdSchema),
    }),
  ),
  beleglage: z.object({
    abgerufen: z.number().int().nonnegative(),
    verwendbar: z.number().int().nonnegative(),
    nicht_verwendbar: z.number().int().nonnegative(),
  }),
  artefakte: z.object({
    markdown: z.object({ vault_path: z.string(), storage_url: z.string() }),
    docx: z.object({ vault_path: z.string(), storage_url: z.string() }),
  }),
})
export type GutachtenResponse = z.infer<typeof gutachtenResponseSchema>
